import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { Box, Button, CircularProgress, Divider, Paper, Stack, Typography } from "@mui/material";
import HelpChatInput from "./HelpChatInput";
import HelpChatMessage from "./HelpChatMessage";
import SuggestedHelpQuestions from "./SuggestedHelpQuestions";
import { sendHelpMessage, submitHelpFeedback } from "./helpService";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";

const WELCOME_MESSAGE = {
  role: "assistant",
  content:
    "Hi! I can help you learn how to use ProspectEngage. Ask me how to complete a task or where to find a feature.",
};

const HELP_CHAT_ERROR_MESSAGE =
  "I'm having trouble connecting to the ProspectEngage Assistant right now. Please try again.";
const MAX_HELP_HISTORY_MESSAGES = 24;
const MAX_HELP_MESSAGE_LENGTH = 4000;

const HELP_PAGE_ROUTES = [
  {
    regex: /^\/home\/?$/,
    context: { path: "/home", page: "Home", module: "General Navigation" },
  },
  {
    regex: /^\/bre-companies\/?$/,
    context: { path: "/bre-companies", page: "BRE Companies", module: "BRE", entity_type: "bre_company" },
  },
  {
    regex: /^\/bre-companies\/new\/?$/,
    context: { path: "/bre-companies/new", page: "New BRE Company", module: "BRE", entity_type: "bre_company" },
  },
  {
    regex: /^\/bre-companies\/([^/]+)\/?$/,
    context: { path: "/bre-companies/:id", page: "BRE Company Details", module: "BRE", entity_type: "bre_company", record_type: "bre_company" },
  },
  {
    regex: /^\/prospecting-companies\/?$/,
    context: {
      path: "/prospecting-companies",
      page: "Prospecting Companies",
      module: "Prospecting",
      entity_type: "prospecting_company",
    },
  },
  {
    regex: /^\/prospecting-companies\/new\/?$/,
    context: {
      path: "/prospecting-companies/new",
      page: "New Prospecting Company",
      module: "Prospecting",
      entity_type: "prospecting_company",
    },
  },
  {
    regex: /^\/prospecting-companies\/([^/]+)\/?$/,
    context: {
      path: "/prospecting-companies/:id",
      page: "Prospecting Company Details",
      module: "Prospecting",
      entity_type: "prospecting_company",
      record_type: "prospecting_company",
    },
  },
  {
    regex: /^\/tradeshows\/?$/,
    context: { path: "/tradeshows", page: "Trade Shows", module: "Prospecting", entity_type: "trade_show" },
  },
  {
    regex: /^\/my-trade-shows\/?$/,
    context: { path: "/my-trade-shows", page: "My Trade Shows", module: "Prospecting", entity_type: "trade_show" },
  },
  {
    regex: /^\/trade-show-agent\/?$/,
    context: {
      path: "/trade-show-agent",
      page: "Trade Show Agent",
      module: "Prospecting",
      entity_type: "trade_show_workspace",
    },
  },
  {
    regex: /^\/trade-show-agent\/workspaces\/([^/]+)\/?$/,
    context: {
      path: "/trade-show-agent/workspaces/:id",
      page: "Trade Show Workspace",
      module: "Prospecting",
      entity_type: "trade_show_workspace",
      record_type: "trade_show_workspace",
    },
  },
];

function getHelpPageContext(pathname) {
  const normalizedPath = pathname || "";
  for (const route of HELP_PAGE_ROUTES) {
    const match = normalizedPath.match(route.regex);
    if (!match) continue;

    const context = { ...route.context };
    if (context.record_type && match[1]) {
      context.record_id = decodeURIComponent(match[1]);
    }
    return context;
  }
  return null;
}

function buildConversationHistory(messages) {
  return messages
    .filter((message) => message.role === "user" || message.role === "assistant")
    .filter((message) => message.content !== WELCOME_MESSAGE.content)
    .filter((message) => !message.isError)
    .map((message) => ({
      role: message.role,
      content: String(message.content || "").trim().slice(0, MAX_HELP_MESSAGE_LENGTH),
    }))
    .filter((message) => message.content)
    .slice(-MAX_HELP_HISTORY_MESSAGES);
}

export default function HelpChatbot({ onHelpLinkClick }) {
  const location = useLocation();
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const conversationEndRef = useRef(null);
  const isLoadingRef = useRef(false);
  const pageContext = useMemo(
    () => getHelpPageContext(location.pathname),
    [location.pathname],
  );

  useEffect(() => {
    conversationEndRef.current?.scrollIntoView({ block: "end" });
  }, [messages, isLoading]);

  const handleSend = async (questionOverride) => {
    const question = (questionOverride ?? input).trim();

    if (!question || isLoadingRef.current) {
      return;
    }

    const conversationHistory = buildConversationHistory(messages);
    const userMessage = {
      role: "user",
      content: question,
    };

    setMessages((currentMessages) => [...currentMessages, userMessage]);
    setInput("");
    isLoadingRef.current = true;
    setIsLoading(true);

    try {
      const payload = {
        message: question,
        messages: conversationHistory,
      };
      if (pageContext) {
        payload.page_context = pageContext;
      }

      const response = await sendHelpMessage(payload);

      const assistantMessage = {
        role: "assistant",
        responseId: response?.responseId,
        content: response?.message || HELP_CHAT_ERROR_MESSAGE,
        links: response?.links || [],
        sources: response?.sources || [],
        confidence: response?.confidence || "none",
      };

      setMessages((currentMessages) => [...currentMessages, assistantMessage]);
    } catch (error) {
      console.error("Help chat request failed", error);
      setMessages((currentMessages) => [
        ...currentMessages,
        {
          role: "assistant",
          content: HELP_CHAT_ERROR_MESSAGE,
          isError: true,
        },
      ]);
    } finally {
      isLoadingRef.current = false;
      setIsLoading(false);
    }
  };

  const showSuggestedQuestions = messages.length === 1 && !isLoading;

  const handleFeedback = async (responseId, rating) => {
    if (!responseId) {
      return;
    }
    await submitHelpFeedback(responseId, rating);
  };

  const handleResetConversation = () => {
    if (isLoadingRef.current) {
      return;
    }
    setMessages([WELCOME_MESSAGE]);
    setInput("");
  };

  const canResetConversation = messages.length > 1 || Boolean(input.trim());

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "stretch", sm: "flex-start" }}
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Typography sx={{ color: BRAND_BLUE, fontWeight: 900, fontSize: "1rem" }}>
            ProspectEngage Assistant
          </Typography>
          <Typography sx={{ color: "#58708a", fontSize: "0.82rem" }}>
            Ask a question about using ProspectEngage.
          </Typography>
        </Box>
        {canResetConversation && (
          <Button
            size="small"
            variant="text"
            startIcon={<RestartAltIcon fontSize="small" />}
            disabled={isLoading}
            onClick={handleResetConversation}
            sx={{
              alignSelf: { xs: "flex-start", sm: "center" },
              color: "rgba(0,51,108,0.72)",
              fontSize: "0.76rem",
              fontWeight: 800,
              textTransform: "none",
            }}
          >
            New conversation
          </Button>
        )}
      </Stack>

      <Paper
        variant="outlined"
        sx={{
          height: { xs: 300, sm: 360 },
          overflowY: "auto",
          p: 1.5,
          borderColor: "rgba(0,51,108,0.14)",
          backgroundColor: "#f8fafc",
        }}
      >
        <Stack spacing={1.25}>
          {messages.map((message, index) => (
            <HelpChatMessage
              key={`${message.role}-${index}`}
              message={message}
              onLinkClick={onHelpLinkClick}
              onFeedback={handleFeedback}
            />
          ))}

          {showSuggestedQuestions && (
            <SuggestedHelpQuestions onSelect={handleSend} disabled={isLoading} />
          )}

          {isLoading && (
            <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 1.1,
                  borderRadius: 2,
                  backgroundColor: "#fff",
                  border: "1px solid rgba(0,51,108,0.12)",
                  color: BRAND_BLUE,
                }}
              >
                  <CircularProgress size={16} sx={{ color: BRAND_GREEN }} />
                <Typography sx={{ fontSize: "0.84rem", fontWeight: 700 }}>
                  ProspectEngage Assistant is thinking...
                </Typography>
              </Box>
            </Box>
          )}
          <Box ref={conversationEndRef} />
        </Stack>
      </Paper>

      <Divider sx={{ borderColor: "rgba(0,51,108,0.08)", my: 1.5 }} />

      <HelpChatInput
        value={input}
        onChange={setInput}
        onSend={handleSend}
        disabled={isLoading}
      />
    </Box>
  );
}
